/**
 * The Add/Configure model form: values, validation (built from the generated
 * AIModelCreate/AIModelUpdate schemas) and the value <-> API body mapping.
 */

import { z } from "zod";
import {
  type AiModelCreate,
  type AiModelResponse,
  type AiModelSuggestResponse,
  type AiModelUpdate,
  type ReasoningConfig,
  schemas,
} from "@/api";
import { i18n } from "@/i18n/i18n";
import { deriveLegacyProvider, endpointFromProvider, hostingOf, protocolOf } from "./modelOptions";

const create = schemas.zAiModelCreate.shape;
const update = schemas.zAiModelUpdate.shape;
const numberFrom = (rule: z.ZodNumber) => z.coerce.number<string>().pipe(rule);
const blankOr = <T extends z.ZodType>(rule: T) => z.union([z.literal(""), rule]);

export const modelFormSchema = z
  .object({
    id: create.id,
    name: create.name,
    deploymentName: create.deploymentName,
    hosting: create.hosting.unwrap().unwrap(),
    apiProtocol: create.apiProtocol.unwrap().unwrap(),
    endpointUrl: create.endpointUrl.unwrap().unwrap(),
    apiKeyValue: update.apiKeyValue.unwrap().unwrap(),
    clearApiKey: z.boolean(),
    contextWindow: numberFrom(create.contextWindow),
    maxOutputTokens: numberFrom(create.maxOutputTokens),
    costPer1KInput: numberFrom(create.costPer1KInput),
    costPer1KOutput: numberFrom(create.costPer1KOutput),
    description: create.description.unwrap().unwrap(),
    costTier: blankOr(create.costTier.unwrap().unwrap()),
    modelType: create.modelType.unwrap().unwrap(),
    isReasoning: z.boolean(),
    supportsVision: z.boolean(),
    supportsFunctionCalling: z.boolean(),
    supportsStreaming: z.boolean(),
    supportsCodeInterpreter: z.boolean(),
    reasoningConfig: schemas.zReasoningConfig.nullable(),
    isEnabled: z.boolean(),
    isDeprecated: z.boolean(),
    sunsetDate: blankOr(update.sunsetDate.unwrap().unwrap()),
    replacementModelId: z.string(),
  })
  .superRefine((value, ctx) => {
    if (value.hosting === "custom" && !value.endpointUrl.trim()) {
      ctx.addIssue({
        code: "custom",
        path: ["endpointUrl"],
        message: i18n.t("adminModels:form.endpointUrlRequired"),
      });
    }
  });

export interface ModelFormValues {
  id: string;
  name: string;
  deploymentName: string;
  hosting: string;
  apiProtocol: string;
  endpointUrl: string;
  apiKeyValue: string;
  clearApiKey: boolean;
  contextWindow: string;
  maxOutputTokens: string;
  costPer1KInput: string;
  costPer1KOutput: string;
  description: string;
  costTier: string;
  modelType: string;
  isReasoning: boolean;
  supportsVision: boolean;
  supportsFunctionCalling: boolean;
  supportsStreaming: boolean;
  supportsCodeInterpreter: boolean;
  reasoningConfig: ReasoningConfig | null;
  isEnabled: boolean;
  isDeprecated: boolean;
  sunsetDate: string;
  replacementModelId: string;
}

export const emptyModelValues = (): ModelFormValues => ({
  id: "",
  name: "",
  deploymentName: "",
  hosting: "azure_openai_eastus2",
  apiProtocol: "openai_responses",
  endpointUrl: "",
  apiKeyValue: "",
  clearApiKey: false,
  contextWindow: "128000",
  maxOutputTokens: "16384",
  costPer1KInput: "0.005",
  costPer1KOutput: "0.015",
  description: "",
  costTier: "",
  modelType: "chat",
  isReasoning: false,
  supportsVision: false,
  supportsFunctionCalling: true,
  supportsStreaming: true,
  supportsCodeInterpreter: false,
  reasoningConfig: null,
  isEnabled: true,
  isDeprecated: false,
  sunsetDate: "",
  replacementModelId: "",
});

export const modelToValues = (model: AiModelResponse): ModelFormValues => ({
  id: model.id,
  name: model.name,
  deploymentName: model.deploymentName,
  hosting: hostingOf(model),
  apiProtocol: protocolOf(model),
  endpointUrl: model.endpointUrl ?? "",
  apiKeyValue: "",
  clearApiKey: false,
  contextWindow: String(model.contextWindow),
  maxOutputTokens: String(model.maxOutputTokens),
  costPer1KInput: String(model.costPer1kInput),
  costPer1KOutput: String(model.costPer1kOutput),
  description: model.description ?? "",
  costTier: model.costTier ?? "",
  modelType: model.modelType ?? "chat",
  isReasoning: model.isReasoning ?? false,
  supportsVision: model.supportsVision ?? false,
  supportsFunctionCalling: model.supportsFunctionCalling !== false,
  supportsStreaming: model.supportsStreaming !== false,
  supportsCodeInterpreter: model.supportsCodeInterpreter ?? false,
  reasoningConfig: model.reasoningConfig ?? null,
  isEnabled: model.isEnabled,
  isDeprecated: model.isDeprecated ?? false,
  sunsetDate: model.sunsetDate ?? "",
  replacementModelId: model.replacementModelId ?? "",
});

/** Pre-fill from an AI suggestion; the endpoint key/URL and description start blank. */
export function suggestionToValues(s: AiModelSuggestResponse): ModelFormValues {
  const derived = endpointFromProvider(s.provider);
  return {
    ...emptyModelValues(),
    id: s.id,
    name: s.name,
    deploymentName: s.deploymentName,
    hosting: s.hosting ?? derived.hosting,
    apiProtocol: s.apiProtocol ?? derived.apiProtocol,
    contextWindow: String(s.contextWindow),
    maxOutputTokens: String(s.maxOutputTokens),
    costPer1KInput: String(s.costPer1kInput ?? 0),
    costPer1KOutput: String(s.costPer1kOutput ?? 0),
    modelType: s.modelType ?? "chat",
    isReasoning: s.isReasoning ?? false,
    supportsVision: s.supportsVision ?? false,
    supportsFunctionCalling: s.supportsFunctionCalling ?? true,
    supportsStreaming: s.supportsStreaming ?? true,
    supportsCodeInterpreter: s.supportsCodeInterpreter ?? false,
    reasoningConfig: s.reasoningConfig ?? null,
    isEnabled: s.isEnabled ?? true,
  };
}

function sharedBody(v: ModelFormValues) {
  return {
    name: v.name.trim(),
    deploymentName: v.deploymentName.trim(),
    hosting: v.hosting,
    apiProtocol: v.apiProtocol,
    provider: deriveLegacyProvider(v.apiProtocol, v.hosting),
    // Presets resolve their endpoint from settings; only "custom" sends a URL.
    endpointUrl: v.hosting === "custom" ? v.endpointUrl.trim() || null : null,
    contextWindow: Number(v.contextWindow),
    maxOutputTokens: Number(v.maxOutputTokens),
    costPer1KInput: Number(v.costPer1KInput),
    costPer1KOutput: Number(v.costPer1KOutput),
    description: v.description.trim() || null,
    costTier: v.costTier || null,
    modelType: v.modelType,
    isReasoning: v.isReasoning,
    supportsVision: v.supportsVision,
    supportsFunctionCalling: v.supportsFunctionCalling,
    supportsStreaming: v.supportsStreaming,
    supportsCodeInterpreter: v.supportsCodeInterpreter,
    reasoningConfig: v.isReasoning ? v.reasoningConfig : null,
    isEnabled: v.isEnabled,
  };
}

export const toCreateBody = (v: ModelFormValues): AiModelCreate => ({ id: v.id.trim(), ...sharedBody(v) });

/** PATCH body. The API key is only sent when typed (or cleared), so unrelated saves never re-encrypt it. */
export function toUpdateBody(v: ModelFormValues): AiModelUpdate {
  const body: AiModelUpdate = {
    ...sharedBody(v),
    isDeprecated: v.isDeprecated,
    // Un-deprecating drops the stale details.
    sunsetDate: (v.isDeprecated && v.sunsetDate) || null,
    replacementModelId: (v.isDeprecated && v.replacementModelId) || null,
  };
  if (v.hosting === "custom" && v.clearApiKey) body.clearApiKey = true;
  else if (v.hosting === "custom" && v.apiKeyValue) body.apiKeyValue = v.apiKeyValue;
  return body;
}
